import React, { useState, useEffect } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { planRequestService } from '../../../services/planRequestService';
import { PlanRequest } from '../../../types/planRequest';
import { useUserAccess } from '../../../context/UserAccessContext';
import { useAuth0Token } from '../../../hooks/useAuth0Token';
import { formatDate } from '../../../utils/dateFormatter';

interface CompletedPlanSelectorProps {
  onSelectPlan: (plan: PlanRequest | null) => void;
  selectedPlan: PlanRequest | null;
}

/**
 * Completed Plan Selector
 *
 * Dropdown/Autocomplete for selecting a completed plan request to revise.
 * Shows client name, completion date, and plan ID.
 */
const CompletedPlanSelector: React.FC<CompletedPlanSelectorProps> = ({
  onSelectPlan,
  selectedPlan
}) => {
  const { customerIds, isAdmin, isDashboardAdmin } = useUserAccess();
  const { token } = useAuth0Token();
  const [plans, setPlans] = useState<PlanRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCompletedPlans();
  }, [token, customerIds]);

  const fetchCompletedPlans = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let allPlans: PlanRequest[] = [];

      if (isAdmin || isDashboardAdmin) {
        // Admins see all completed plans - both statuses must be 'completed'
        allPlans = await planRequestService.getAllRequests(token, {
          sipsStatus: 'completed',
          outputStatus: 'completed'
        });
      } else {
        // Regular users see only their assigned customer IDs
        const planPromises = customerIds.map(customerId =>
          planRequestService.getMyRequests(customerId, token)
        );
        const results = await Promise.all(planPromises);
        allPlans = results.flat();

        // Filter to only completed plans ('not-requested' means no presentation needed, treat as completed)
        allPlans = allPlans.filter(plan =>
          plan.sipsStatus === 'completed' && (plan.outputStatus === 'completed' || plan.outputStatus === 'not-requested')
        );
      }

      // Sort by completion date (most recent first)
      allPlans.sort((a, b) => {
        const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return dateB - dateA;
      });

      setPlans(allPlans);
    } catch (err: any) {
      console.error('Error fetching completed plans:', err);
      setError(err.message || 'Failed to load completed plans');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (plans.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>No Completed Plans Available</strong>
        </Typography>
        <Typography variant="caption">
          There are no completed plans available to revise. Please select a different option or create a new plan from scratch.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Select a completed plan to use as a starting point for this request. All data will be pre-filled and you can make any necessary changes.
      </Typography>

      <Autocomplete
        options={plans}
        value={selectedPlan}
        onChange={(_, newValue) => onSelectPlan(newValue)}
        getOptionLabel={(option) => option.clientName}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Completed Plan"
            placeholder="Start typing client name..."
            required
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body1">
                  {option.clientName}
                </Typography>
                {option.isRevision && (
                  <Chip label="Revision" size="small" color="info" />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Completed: {option.completedAt ? formatDate(option.completedAt) : 'Unknown'}
                {' • '}
                Plan ID: {option.id.substring(0, 8)}
                {option.planTitle && ` • ${option.planTitle}`}
              </Typography>
            </Box>
          </Box>
        )}
        sx={{ mt: 2 }}
      />
    </Box>
  );
};

export default CompletedPlanSelector;
